'use client'

import * as React from 'react'
import { Tooltip as TooltipPrimitive } from 'radix-ui'

import { cn } from '@/lib/utils'

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

/**
 * Concept `.act[data-tip]::after` — card 7px below the trigger, 12px/500
 * ink, hairline border, 8px radius, float shadow, fading in while rising
 * 3px over 120ms. No arrow. Surface is `bg-card` (light = white, dark =
 * `--card`) so ink stays readable when `[data-theme=dark]` flips
 * `--color-text`.
 */
function TooltipContent({
  className,
  side = 'bottom',
  sideOffset = 7,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        side={side}
        sideOffset={sideOffset}
        className={cn(
          // z-[110]: tooltips are ephemeral and must top every layer — at
          // z-50 they rendered BEHIND the ⌘K palette overlay (z-[100]).
          'border-border text-text shadow-tip animate-in fade-in-0 slide-in-from-bottom-[3px] bg-card z-[110] w-fit rounded-[8px] border px-[9px] py-1.5 text-xs leading-none font-medium whitespace-nowrap duration-120 ease-out dark:shadow-black/40',
          className,
        )}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
